import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { fetchTimeline, addTimelineEntry, uploadImage } from "@/lib/dashboardApi";
import {
  Phone, StickyNote, BellRing, Image as ImageIcon, MessageSquare, FileText, Wrench,
  CalendarClock, DollarSign, BadgeCheck, PackageCheck, Activity, Mail, Loader2, Plus, X, ExternalLink,
} from "lucide-react";

const ICONS = {
  phone: Phone, "sticky-note": StickyNote, "bell-ring": BellRing, image: ImageIcon,
  "message-square": MessageSquare, "file-text": FileText, wrench: Wrench, "calendar-clock": CalendarClock,
  "dollar-sign": DollarSign, "badge-check": BadgeCheck, "package-check": PackageCheck, activity: Activity, mail: Mail,
};

const CATS = {
  communication: { label: "Communication", dot: "bg-blue-500", chip: "text-blue-700 bg-blue-50 border-blue-200" },
  job: { label: "Job / Service", dot: "bg-orange-500", chip: "text-orange-700 bg-orange-50 border-orange-200" },
  financial: { label: "Financial / Proposal", dot: "bg-green-600", chip: "text-green-700 bg-green-50 border-green-200" },
  media: { label: "Media / Docs", dot: "bg-purple-500", chip: "text-purple-700 bg-purple-50 border-purple-200" },
  manual: { label: "Note", dot: "bg-slate-400", chip: "text-slate-700 bg-slate-50 border-slate-200" },
  system: { label: "System", dot: "bg-slate-300", chip: "text-slate-600 bg-slate-50 border-slate-200" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "communication", label: "Communication" },
  { key: "job", label: "Job / Service" },
  { key: "financial", label: "Financial" },
  { key: "media", label: "Media" },
  { key: "manual", label: "Notes" },
];

const SECTION_LABEL = {
  "launch-select": "Proposal", forms: "Document", dispatch: "Job", "warranty-compliance": "Warranty",
  invoices: "Invoice", membership: "Membership", leads: "Lead",
};

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 16);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const dayKey = (iso) => { const d = new Date(iso); return isNaN(d) ? "Earlier" : d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); };

export default function CustomerTimeline({ customerId, customerName, compact = false }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null); // {kind}

  const load = useCallback(async () => {
    setLoading(true);
    try { setEvents(await fetchTimeline(customerId)); }
    catch (e) { console.error("timeline load failed", e); toast.error("Could not load timeline."); }
    finally { setLoading(false); }
  }, [customerId]);
  useEffect(() => { if (customerId) load(); }, [customerId, load]);

  const shown = (filter === "all" ? events : events.filter((e) => e.category === filter));
  const list = compact ? shown.slice(0, 8) : shown;

  const groups = list.reduce((acc, e) => {
    const k = dayKey(e.at);
    (acc[k] = acc[k] || []).push(e);
    return acc;
  }, {});

  return (
    <div data-testid="customer-timeline">
      {!compact && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`timeline-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setModal({ kind: "call" })} data-testid="timeline-log-call" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:border-[#F97316]"><Phone className="w-4 h-4 text-blue-500" /> Log Call</button>
            <button onClick={() => setModal({ kind: "note" })} data-testid="timeline-add-note" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:border-[#F97316]"><StickyNote className="w-4 h-4 text-slate-500" /> Add Note</button>
            <button onClick={() => setModal({ kind: "follow_up" })} data-testid="timeline-add-followup" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:border-[#F97316]"><BellRing className="w-4 h-4 text-amber-500" /> Follow-Up</button>
            <button onClick={() => setModal({ kind: "media" })} data-testid="timeline-upload" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:border-[#F97316]"><ImageIcon className="w-4 h-4 text-purple-500" /> Upload</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-xl bg-slate-50/60" data-testid="timeline-empty">
          <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 text-sm font-medium">No activity yet for this customer.</p>
          {!compact && <p className="text-slate-400 text-xs mt-1">Calls, texts, proposals, jobs, invoices and warranty events will appear here automatically.</p>}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([day, items]) => (
            <div key={day}>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">{day}</p>
              <ol className="relative border-l-2 border-slate-100 ml-2 space-y-4">
                {items.map((e) => {
                  const cat = CATS[e.category] || CATS.system;
                  const Icon = ICONS[e.icon] || Activity;
                  return (
                    <li key={e.id} className="ml-5 relative" data-testid={`timeline-event-${e.event}`}>
                      <span className={`absolute -left-[26px] top-1 w-3.5 h-3.5 rounded-full ring-4 ring-white ${cat.dot}`} />
                      <div className="bg-white border border-slate-200 rounded-lg p-3.5 hover:border-slate-300 transition-colors">
                        <div className="flex items-start gap-3">
                          <Icon className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-800 text-sm">{e.title}</p>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${cat.chip}`}>{cat.label}</span>
                            </div>
                            {e.body && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{e.body}</p>}
                            {e.media_url && (
                              <a href={e.media_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-purple-600 hover:underline">
                                <ImageIcon className="w-3.5 h-3.5" /> View attachment
                              </a>
                            )}
                            {e.due_at && <p className="text-xs text-amber-600 mt-1 font-medium">Due: {fmt(e.due_at)}</p>}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-400">
                              <span>{fmt(e.at)}</span>
                              {e.source?.name && <span>· {e.source.kind === "customer" ? "Customer" : e.source.kind === "system" ? "System" : e.source.name}</span>}
                              {e.ref?.section && (
                                <a href={`/admin/${e.ref.section}`} data-testid={`timeline-ref-${e.event}`} className="inline-flex items-center gap-0.5 text-[#F97316] font-semibold hover:underline">
                                  View {SECTION_LABEL[e.ref.section] || "record"} <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>
      )}

      {modal && <EntryModal kind={modal.kind} customerId={customerId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function EntryModal({ kind, customerId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [due, setDue] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const LABELS = { call: "Log a Call", note: "Add a Note", follow_up: "Add a Follow-Up", media: "Upload Photo / Document" };

  const save = async () => {
    setBusy(true);
    try {
      let media_url = "";
      if (kind === "media") {
        if (!file) { toast.error("Choose a file first."); setBusy(false); return; }
        const { url } = await uploadImage(file);
        media_url = url;
      }
      await addTimelineEntry(customerId, { kind, title: title.trim(), body: body.trim(), due_at: due, media_url });
      toast.success("Added to timeline.");
      onSaved();
    } catch (e) { console.error(e); toast.error("Could not save entry."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="timeline-entry-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{LABELS[kind]}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-3">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === "call" ? "Call summary (e.g. Left voicemail)" : "Title"} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="timeline-entry-title" />
          {kind === "media" ? (
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm" data-testid="timeline-entry-file" />
          ) : (
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Details…" rows={3} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="timeline-entry-body" />
          )}
          {kind === "follow_up" && (
            <div>
              <label className="text-xs font-semibold text-slate-500">Follow-up date</label>
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm mt-1" data-testid="timeline-entry-due" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600">Cancel</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60 bg-[#F97316] hover:bg-[#ea6a0c]" data-testid="timeline-entry-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
